import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { Link, useHistory } from 'react-router-dom'
import { XIcon, XCircleIcon } from '@heroicons/react/outline'
import { topBarStore } from '../../../lib/stores/TopBarStore'
import StoreContext from '../../providers/storesContext'
import {caseDetailUrl, caseReleaseUrl, HOME} from '../../routing/paths'
import assistedReleaseRemainingTime from '../../util/assistedReleaseRemainingTime'
import SuccessMessage from '../../components/SuccessMessage'
import Button from '../../components/Button'
import Error500 from '../../scenes/Error500'
import Modal from '../../components/Modal'
import FrequentlyAskedQuestionsModal from '../../components/FAQ'

const CaseSuccess = () => {
  const { caseDetailStore } = useContext(StoreContext)
  const history = useHistory()
  const { immobilizationCase } = caseDetailStore
  const [viewCancelCashModal, setViewCancelCashModal] = useState(false)
  const [viewFAQModal, setViewFAQModal] = useState(false)

  const showCancelCashModal = (e) => {
    e.preventDefault()
    if (caseDetailStore.cancelingCashPayment) {
      return;
    }
    setViewCancelCashModal(true);
  }

  const hideCancelCashModal = () => {
    caseDetailStore.clearServerError()
    setViewCancelCashModal(false)
  }

  const showFAQModal = (e) => {
    e.stopPropagation()
    e.preventDefault()
    setViewFAQModal(true)
  }

  const hideFAQModal = () => {
    setViewFAQModal(false)
  }

  const returnToHome = () => {
    location.href = HOME
  }

  const cancelCashPayment = () => {
    caseDetailStore.cancelCashPayment().then((success) => {
      if (success) {
        location.href = caseDetailUrl(immobilizationCase)
      }
    })
  }

  useEffect(() => {
    if (!immobilizationCase.isPaid && !immobilizationCase.isCash) {
      history.push(caseDetailUrl(immobilizationCase))
    }

    topBarStore.setTitle('')
  }, [])

  if (immobilizationCase.isSelfRelease || immobilizationCase.isBootboxed) {
    return (
          <SuccessMessage
            subtitle={<div className='text-gray-600'>You will receive a code to unlock the boot via sms</div>}
            title="Your payment has been successfully completed!"
            footer={
              <>
                <Button as={Link} to={caseReleaseUrl(immobilizationCase)}>
                  Start self release
                </Button>
                <span className='mt-3'>
                  Questions? Read our{' '}
                  <b className="cursor-pointer text-green-500" onClick={showFAQModal}>
                    FAQ
                  </b>
                </span>
                <FrequentlyAskedQuestionsModal
                  faq={immobilizationCase.operationVariables.faq}
                  open={viewFAQModal}
                  handleClose={hideFAQModal}
                />
              </>
            }
          />
    )
  }

  if (immobilizationCase.isAssistedRelease || immobilizationCase.isCash) {
    const tAssist = assistedReleaseRemainingTime()
    return (
      <>
        <SuccessMessage
          subtitle={<div className='text-gray-600'>
            The estimated time for the boot release is <span className='whitespace-nowrap'>{tAssist}</span>. We will notify you by SMS when it’s completed.
            </div>}
          title={
            immobilizationCase.isAssistedRelease
              ? 'Your payment has been successfully completed!'
              : 'Your cash payment has been successfully requested'
          }
          footer={
            <>
              {immobilizationCase.isCash && (<>
                <Button
                  onClick={returnToHome}
                  isLoading={caseDetailStore.cancelingCashPayment}
                >
                Return to home
                </Button>
                <span className="mt-2 text-gray-600">or <a href="" onClick={showCancelCashModal} className="text-green-400 font-bold">
                  Cancel your cash payment request.
                </a></span>
              </>)}
              <span className="mt-1 text-gray-600">
                Questions? Read our{' '}
                <b className="cursor-pointer" onClick={showFAQModal}>
                  FAQ
                </b>
              </span>
              <FrequentlyAskedQuestionsModal
                faq={immobilizationCase.operationVariables.faq}
                open={viewFAQModal}
                handleClose={hideFAQModal}
              />
            </>
          }
        />
        <Modal noButtons onCancel={hideCancelCashModal} open={viewCancelCashModal} small>
          <div className="flex-1">
            <div className="font-bold text-center text-lg">Cancel Cash Payment</div>
            {caseDetailStore.serverError && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{caseDetailStore.serverError}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="text-center mt-4">
              Are you sure that you want to cancel your cash payment?
            </div>
            <div className="mt-4">
              <Button
                onClick={cancelCashPayment}
                extraStyle="w-full"
                isLoading={caseDetailStore.cancelingCashPayment}
              >
                Cancel my cash payment request
              </Button>
            </div>
            <div className="mt-4 text-gray-400 text-center">
              *Please only cancel your cash request if you will pay with credit card
            </div>
          </div>
        </Modal>
      </>
    )
  }

  if (immobilizationCase.isSolved) {
    return (
      <>
        <SuccessMessage
          subtitle={<div className='text-gray-600'>Thank you for working with BootVision</div>}
          title="Your payment has been successfully completed!"
          footer={
            <span>
              Questions? Read our{' '}
              <b className="cursor-pointer" onClick={showFAQModal}>
                FAQ
              </b>
            </span>
          }
        />
        <FrequentlyAskedQuestionsModal
          faq={immobilizationCase.operationVariables.faq}
          open={viewFAQModal}
          handleClose={hideFAQModal}
        />
      </>
    )
  }

  return <Error500 />
}

export default observer(CaseSuccess)
